import Layout from '../components/Layout';
import TagVisual from '../components/TagVisual';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, QrCode, LifeBuoy, Wallet, Wrench, Play, Star, Store, Truck, Building2, Check, X as XIcon, Award, Network, Gift, Copy, Sparkles, Handshake, ChevronDown, Trophy, Crown, Medal } from 'lucide-react';
import { TRUST_BADGES, APP_FEATURES, TESTIMONIALS, PARTNER_TYPES, COMPARISON, FOUNDER, PARTNER_FAQS } from '../mock';
import { useState, useEffect } from 'react';
import axios from 'axios';
import * as Icons from 'lucide-react';
import PartnerContactModal from '../components/PartnerContactModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const iconMap = { ShieldCheck, QrCode, LifeBuoy, Wallet, Wrench, Store, Truck, Building2, Award, Network };

const SMART_COMPARE_ROWS = [
  { f: 'Privacy & Emergency Calls', std: 'Basic call routing', cnm: '100% Masked Calling & SOS Alerts', stdOk: true },
  { f: 'Medical Info (Blood Group)', std: 'Not available', cnm: 'Emergency Blood Group & Profile' },
  { f: '24×7 Road Side Assistance', std: 'Not available', cnm: 'Instant RSA Support Routing' },
  { f: 'Automated Reminders', std: 'Not available', cnm: 'PUC, Insurance & Service Alerts' },
  { f: 'Fuel & Mileage Tracker', std: 'Not available', cnm: 'Smart Fuel Logs & Expense Manager' },
  { f: 'Digital Service History', std: 'Not available', cnm: 'Complete Digital Maintenance Records' },
  { f: 'Insurance Claim Support', std: 'Not available', cnm: 'End-to-End Claim Process Guidance' },
  { f: 'Document Vault', std: 'Not available', cnm: 'Secure Digital RC, DL & Policy Vault' },
];

const COMMON_FAQS = [
  { q: 'How is my number kept private?', a: 'Every call, SMS and WhatsApp message is routed through a masked relay number. Your real phone number is never shared with the person scanning your tag.' },
  { q: 'Is the tag waterproof?', a: 'Yes. Every Connect Near Me tag is IP67 rated and tested for extreme Indian weather.' },
  { q: 'What is included in RSA help?', a: 'Roadside assistance across 500+ cities — towing, flat tyre, jump-start, minor repairs and fuel delivery.' },
  { q: 'How do I activate my tag after receiving it?', a: 'Visit /activate, enter the Tag ID printed on your sticker and the Activation Code from the pouch. Verify OTP on your mobile, fill in your vehicle and emergency details — the tag becomes ACTIVE instantly.' },
  { q: 'What happens if I lose my activation code?', a: 'On the activation page tap "Need Help?", pick your Order ID and submit a request. Our team verifies your ownership within 24 hours and lets you continue activation with OTP-only verification.' },
  { q: 'What information is visible on the public emergency page?', a: 'Only vehicle basics (make, model, colour, registration), blood group and medical info you flag as public, and an emergency contact name. Your full mobile number is never shown — we route calls through a masked bridge.' },
  { q: 'Which payment methods are supported?', a: 'UPI, cards, netbanking, wallets (via Cashfree) and Cash on Delivery across India. You can also apply Connect Near Me wallet points at checkout.' },
  { q: 'What is the return policy?', a: 'Returns can be initiated within 15 days from delivery. A nominal Rs.100 return fee applies. You receive a credit note (usable on future orders); we don\u2019t offer monetary refunds. Damaged/defective items are exchanged free.' },
];

export default function HomePage() {
  const [videoOpen, setVideoOpen] = useState(false);
  const [partnerModalOpen, setPartnerModalOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [leaderboard, setLeaderboard] = useState({ by_commission: [], by_orders: [] });
  const [lbMetric, setLbMetric] = useState('commission');

  useEffect(() => {
    axios.get(`${API}/partner/leaderboard?limit=5`).then((r) => setLeaderboard(r.data)).catch(() => {});
  }, []);

  return (
    <Layout>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-14 pb-20 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-400 to-orange-500 text-white px-4 py-2 rounded-full text-xs font-bold shadow-lg animate-pulse" data-testid="hero-startup-offer-badge">
              <Sparkles size={14} /> 50% Startup Offer — Limited Time!
            </div>
            <h1 className="h-display text-5xl md:text-6xl lg:text-7xl mt-4">
              Your All-in-One Vehicle&apos;s <em className="font-serif-display italic font-normal text-navy">Digital Shield</em>
            </h1>
            <p className="mt-6 text-lg text-neutral-600 max-w-xl">
              Protect your Bike &amp; Car with Smart QR Technology. Instant emergency alerts, privacy protection, and reliable RSA support—all in one tag.
            </p>
            <div className="mt-5 inline-flex items-center gap-2 bg-navy/5 border border-navy/10 rounded-lg px-3 py-2 text-sm text-navy font-semibold" data-testid="hero-launch-special-strip">
              <Gift size={14} className="text-orange-500" /> Launch Special: <span className="font-bold">50% OFF</span> on all plans
            </div>
            <div className="mt-8 flex gap-3 flex-wrap">
              <Link to="/shop" className="btn-primary text-base" data-testid="hero-buy-tag-btn">Buy your tag <ArrowRight size={16} /></Link>
              <Link to="/how-it-works" className="btn-outline text-base" data-testid="hero-how-it-works-btn">How it works</Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-neutral-600">
              <div className="flex items-center gap-1.5">
                {[...Array(5)].map((_, i) => <Star key={i} size={14} className="fill-yellow-400 text-yellow-400" />)}
                <span className="ml-1">4.7 / 5 from 12,000+ owners</span>
              </div>
            </div>
          </div>
          <div className="relative flex justify-center">
            <TagVisual size={320} />
            <div className="absolute -bottom-6 -left-2 md:-left-6 bg-ink text-white rounded-2xl p-4 shadow-2xl">
              <p className="text-xs text-neutral-400 line-through">MRP &#8377;599</p>
              <p className="text-2xl font-bold">&#8377;299 <span className="text-xs text-orange-400 font-semibold">50% OFF</span></p>
              <p className="text-xs text-neutral-400 mt-1">Bike Tag</p>
            </div>
            <div className="absolute -top-4 right-2 md:-right-4 bg-white border border-navy/15 rounded-2xl p-4 shadow-xl rotate-3">
              <p className="text-xs font-semibold text-navy">Free delivery</p>
              <p className="text-xs text-neutral-600">+ COD available</p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {TRUST_BADGES.map((t) => {
              const Ic = iconMap[t.icon] || ShieldCheck;
              return (
                <div key={t.title} className="card-soft p-4 flex flex-col items-center text-center hover:-translate-y-0.5 transition-transform">
                  <div className="w-11 h-11 rounded-xl bg-navy/5 text-navy flex items-center justify-center mb-3">
                    <Ic size={20} />
                  </div>
                  <p className="text-sm font-semibold leading-tight">{t.title}</p>
                  <p className="text-[11px] text-neutral-500 mt-1 leading-snug">{t.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <p className="eyebrow mb-4">What&apos;s inside</p>
            <h2 className="h-display text-4xl md:text-5xl">Everything an owner needs, in <em className="font-serif-display italic font-normal text-navy">one tag</em></h2>
          </div>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4">
            {APP_FEATURES.map((f) => {
              const Ic = Icons[f.icon] || Icons.ShieldCheck;
              return (
                <div key={f.title} className="rounded-2xl p-5 hover:-translate-y-1 transition-transform shadow-lg" style={{background: 'linear-gradient(150deg, #0B2E6F 0%, #071E4A 100%)'}}>
                  <div className="w-10 h-10 rounded-lg bg-white/15 text-white flex items-center justify-center mb-3"><Ic size={18} /></div>
                  <h3 className="text-sm font-semibold text-white">{f.title}</h3>
                  <p className="text-xs text-blue-200 mt-1 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Become Partner */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <p className="eyebrow mb-4">Become a partner</p>
            <h2 className="h-display text-4xl md:text-5xl">Grow Your Automotive Business with <em className="font-serif-display italic font-normal text-navy">Connect Near Me</em></h2>
            <p className="mt-5 text-neutral-600">Pick a partnership that fits your scale. Retail, distribute, or open a franchise outlet.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-5 mt-12">
            {PARTNER_TYPES.map((p, i) => {
              const Ic = iconMap[p.icon] || Store;
              return (
                <div key={p.id} className={`card-soft p-7 flex flex-col ${i === 1 ? 'bg-navy text-white border-navy' : ''}`}>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${i === 1 ? 'bg-white/15' : 'bg-navy/5 text-navy'}`}>
                    <Ic size={22} />
                  </div>
                  <h3 className="text-2xl font-semibold">{p.title}</h3>
                  <p className={`text-sm mt-1 ${i === 1 ? 'text-blue-200' : 'text-neutral-500'}`}>{p.tagline}</p>
                  <div className={`mt-4 text-xs font-medium tracking-wide uppercase ${i === 1 ? 'text-blue-200' : 'text-navy'}`}>{p.price}</div>
                  <ul className="mt-5 space-y-2 flex-1">
                    {p.perks.map((it) => (
                      <li key={it} className={`text-sm flex gap-2 items-start ${i === 1 ? 'text-blue-100' : 'text-neutral-700'}`}>
                        <span className={`mt-1.5 w-1 h-1 rounded-full ${i === 1 ? 'bg-white' : 'bg-navy'}`}></span> {it}
                      </li>
                    ))}
                  </ul>
                  <Link to="/partner" className={`mt-6 inline-flex items-center justify-center gap-2 rounded-full py-2.5 px-4 font-semibold text-sm ${i === 1 ? 'bg-white text-navy hover:bg-blue-50' : 'bg-navy text-white hover:bg-navy-deep'} transition-colors`}>
                    Apply now <ArrowRight size={14} />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Become a Partner CTA Banner */}
      <section className="py-16 relative overflow-hidden" style={{background: 'linear-gradient(135deg, #0B2E6F 0%, #071E4A 60%, #030A1F 100%)'}} data-testid="partner-cta-banner">
        <div className="absolute -left-24 -top-24 w-96 h-96 rounded-full bg-white/5"></div>
        <div className="absolute -right-24 -bottom-24 w-96 h-96 rounded-full bg-white/5"></div>
        <div className="max-w-5xl mx-auto px-6 relative">
          <div className="grid md:grid-cols-5 gap-8 items-center text-white">
            <div className="md:col-span-3">
              <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-3 py-1 text-xs mb-4 backdrop-blur-sm">
                <Handshake size={12} /> Grow with us
              </div>
              <h2 className="h-display text-3xl md:text-4xl lg:text-5xl leading-tight">
                Become a <em className="font-serif-display italic font-normal text-orange-300">Connect Near Me</em> Partner
              </h2>
              <p className="mt-4 text-blue-100 text-base leading-relaxed">
                Join India&apos;s fastest-growing smart-QR vehicle network. Earn high margins, get exclusive territory rights, and build a lasting recurring revenue stream — with full training &amp; marketing support.
              </p>
              <div className="mt-6 grid grid-cols-2 gap-4 max-w-md">
                <div><p className="h-display text-2xl text-orange-300">100+</p><p className="text-xs text-blue-200">Cities live</p></div>
                <div><p className="h-display text-2xl text-orange-300">₹0</p><p className="text-xs text-blue-200">Setup cost</p></div>
              </div>
            </div>
            <div className="md:col-span-2 flex flex-col gap-3">
              <button
                onClick={() => setPartnerModalOpen(true)}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-navy py-3.5 px-6 font-bold text-base hover:bg-orange-100 transition-colors shadow-xl"
                data-testid="partner-cta-join-btn"
              >
                Join / Contact Us <ArrowRight size={16} />
              </button>
              <Link
                to="/partner"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 py-3 px-6 font-semibold text-sm hover:bg-white/10 text-white transition-colors"
                data-testid="partner-cta-learn-more-link"
              >
                Learn more about plans
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Common FAQs */}
      <section className="py-24 bg-white" data-testid="common-faqs-section">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center">
            <p className="eyebrow mb-4">FAQ</p>
            <h2 className="h-display text-4xl md:text-5xl">Frequently asked <em className="font-serif-display italic font-normal text-navy">questions</em></h2>
            <p className="mt-4 text-neutral-600">Everything about the tag, activation, RSA, refunds and payments.</p>
          </div>
          <div className="mt-12 space-y-3">
            {COMMON_FAQS.map((f, idx) => (
              <div key={f.q} className="card-soft overflow-hidden" data-testid={`common-faq-item-${idx}`}>
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? -1 : idx)}
                  className="w-full flex items-center justify-between text-left p-5 hover:bg-neutral-50 transition-colors"
                  data-testid={`common-faq-toggle-${idx}`}
                >
                  <span className="font-semibold text-neutral-900 pr-4">{f.q}</span>
                  <ChevronDown size={18} className={`text-navy shrink-0 transition-transform ${openFaq === idx ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === idx && (
                  <div className="px-5 pb-5 pt-0 text-sm text-neutral-600 leading-relaxed" data-testid={`common-faq-answer-${idx}`}>
                    {f.a}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/faq" className="btn-primary text-base" data-testid="common-faqs-all-btn">
              See all FAQs <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Founder Profile */}
      <section className="py-20 bg-white" data-testid="founder-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-5 gap-8 items-center">
            <div className="md:col-span-2">
              <div className="aspect-[3/4] rounded-3xl overflow-hidden relative shadow-xl">
                {FOUNDER.photo ? (
                  <img src={FOUNDER.photo} alt={FOUNDER.name} className="w-full h-full object-cover" data-testid="founder-photo" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-navy via-navy-deep to-ink flex items-center justify-center text-white">
                    <span className="h-display text-7xl opacity-30">DD</span>
                  </div>
                )}
              </div>
            </div>
            <div className="md:col-span-3">
              <p className="eyebrow mb-3">Meet our founder &amp; visionary</p>
              <h2 className="h-display text-4xl md:text-5xl">{FOUNDER.name}</h2>
              <p className="text-navy font-medium mt-2 text-sm tracking-wider uppercase">{FOUNDER.role}</p>
              <p className="mt-5 text-neutral-700 leading-relaxed text-[15px]">{FOUNDER.bio}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <p className="eyebrow mb-4">How we stack up</p>
            <h2 className="h-display text-4xl md:text-5xl">Same problem, <em className="font-serif-display italic font-normal text-navy">smarter solution.</em></h2>
            <p className="mt-4 text-neutral-600">More features, same price. See how Connect Near Me compares to other QR tag brands.</p>
          </div>

          <div className="mt-12 card-soft overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-4 px-4 md:px-6 font-semibold text-neutral-500 uppercase tracking-wider text-xs">Feature / Price</th>
                    {COMPARISON.columns.map((c) => (
                      <th key={c.key} className={`text-center py-4 px-3 font-semibold ${c.highlight ? 'bg-navy text-white' : 'text-neutral-700'}`}>{c.name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON.rows.map((r, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-3.5 px-4 md:px-6 text-neutral-700">{r.feature}</td>
                      {COMPARISON.columns.map((c) => {
                        const v = r[c.key];
                        return (
                          <td key={c.key} className={`py-3.5 px-3 text-center ${c.highlight ? 'bg-navy/[0.04]' : ''}`}>
                            {v === true ? <Check size={16} className={c.highlight ? 'text-navy mx-auto' : 'text-green-600 mx-auto'} />
                              : v === false ? <XIcon size={16} className="text-neutral-300 mx-auto" />
                              : <span className={`font-semibold ${c.highlight ? 'text-navy' : ''}`}>{v}</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-xs text-neutral-500 text-center mt-4 max-w-3xl mx-auto">Note: Pricing and feature information for competitors is collected from their official public websites and product listings. All trademarks belong to their respective owners. We compare in good faith to help customers make an informed choice.</p>
        </div>
      </section>

      {/* Standard vs Smart Tag — Side-by-side Value Comparison */}
      <section className="py-24 bg-gradient-to-b from-white to-cream" data-testid="smart-vs-standard">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-navy/10 text-navy px-3 py-1 rounded-full text-xs font-semibold mb-4"><Sparkles size={12} /> The difference is night &amp; day</div>
            <h2 className="h-display text-4xl md:text-5xl">Why settle for a <em className="font-serif-display italic font-normal text-navy">plain QR</em> when you can own a <span className="text-orange-500">Smart Shield?</span></h2>
            <p className="mt-4 text-neutral-600 text-lg">Same sticker on the outside. A whole safety command centre on the inside.</p>
          </div>

          <div className="mt-14 grid md:grid-cols-2 gap-6 items-stretch">
            {/* Standard Market Tag — muted / weak */}
            <div className="rounded-2xl border-2 border-neutral-200 bg-white p-7 relative overflow-hidden flex flex-col" data-testid="col-standard">
              <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-neutral-100"></div>
              <div className="relative">
                <div className="inline-flex items-center gap-2 bg-neutral-100 text-neutral-500 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider mb-4"><XIcon size={11} /> Standard Market</div>
                <h3 className="h-display text-2xl text-neutral-500">Basic QR Tags</h3>
                <p className="text-sm text-neutral-400 mt-1">A sticker. That's it.</p>
              </div>
              <ul className="mt-6 space-y-4 relative flex-1">
                {SMART_COMPARE_ROWS.map((r) => (
                  <li key={r.f} className="flex items-start gap-3 pb-4 border-b border-neutral-100 last:border-0">
                    <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center ${r.stdOk ? 'bg-green-100 text-green-600' : 'bg-neutral-100 text-neutral-400'}`}>
                      {r.stdOk ? <Check size={14} strokeWidth={3} /> : <XIcon size={14} />}
                    </div>
                    <div>
                      <p className={`font-semibold text-sm ${r.stdOk ? 'text-neutral-700' : 'text-neutral-500'}`}>{r.f}</p>
                      <p className="text-xs text-neutral-400 mt-0.5">{r.std}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* CNM Smart Tag — hero */}
            <div className="rounded-2xl p-7 relative overflow-hidden flex flex-col text-white shadow-[0_20px_50px_-15px_rgba(11,46,111,0.6)]" style={{background: 'linear-gradient(140deg, #0B2E6F 0%, #071E4A 55%, #030A1F 100%)'}} data-testid="col-cnm">
              <div className="absolute -right-14 -top-14 w-56 h-56 rounded-full bg-orange-500/15 blur-2xl"></div>
              <div className="absolute -left-14 -bottom-14 w-56 h-56 rounded-full bg-orange-400/10 blur-3xl"></div>
              <div className="relative">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-yellow-400 text-black px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider mb-4"><ShieldCheck size={11} /> Connect Smart Shield</div>
                <h3 className="h-display text-2xl">CONNECT Near Me — <em className="font-serif-display italic font-normal text-orange-300">Smart Tag</em></h3>
                <p className="text-sm text-blue-200 mt-1">Your invisible co-driver, 24×7.</p>
              </div>
              <ul className="mt-6 space-y-4 relative flex-1">
                {SMART_COMPARE_ROWS.map((r) => (
                  <li key={r.f} className="flex items-start gap-3 pb-4 border-b border-white/10 last:border-0">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-yellow-400 flex-shrink-0 flex items-center justify-center text-black"><Check size={14} strokeWidth={3} /></div>
                    <div>
                      <p className="font-semibold text-white text-sm">{r.f}</p>
                      <p className="text-xs text-orange-200/90 mt-0.5">{r.cnm}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Value proposition banner */}
          <div className="mt-10 rounded-2xl p-7 md:p-9 relative overflow-hidden text-white" style={{background: 'linear-gradient(135deg, #F97316 0%, #EA580C 55%, #B45309 100%)'}} data-testid="value-banner">
            <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-yellow-300/20 blur-2xl"></div>
            <div className="relative max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-widest mb-4"><Sparkles size={11} /> The all-in-one edge</div>
              <p className="h-display text-2xl md:text-3xl leading-snug font-serif-display italic">"In today's busy schedule, managing your car's service dates, insurance, fuel logs, and safety is exhausting. Let this small smart sticker handle everything for you 24/7. Your ultimate, all-in-one vehicle companion!"</p>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-10 text-center" data-testid="smart-cta">
            <Link to="/shop" className="inline-flex items-center gap-3 bg-navy hover:bg-navy/90 text-white font-bold px-8 py-4 rounded-full text-lg shadow-[0_10px_30px_-10px_rgba(11,46,111,0.7)] transition-all hover:-translate-y-0.5" data-testid="smart-cta-btn">
              <ShieldCheck size={20} /> Get Your Smart Shield Tag Now @ ₹299 <ArrowRight size={18} />
            </Link>
            <p className="mt-3 text-xs text-neutral-500 uppercase tracking-wider font-semibold">Includes <span className="text-orange-600">1-Year Full VIP Access</span></p>
          </div>
        </div>
      </section>

      {/* Refer & Earn */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="card-soft p-8 md:p-10 bg-navy text-white border-navy grid md:grid-cols-2 gap-8 items-center overflow-hidden relative">
            <div className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-white/5"></div>
            <div className="absolute -right-8 -bottom-24 w-72 h-72 rounded-full bg-white/5"></div>
            <div className="relative">
              <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-3 py-1 text-xs mb-4">
                <Gift size={12} /> Refer & Earn
              </div>
              <h2 className="h-display text-3xl md:text-4xl">Bring a friend, <em className="font-serif-display italic font-normal text-blue-200">earn cashback.</em></h2>
              <p className="mt-3 text-blue-100">Share your unique link. Every friend who buys a Connect tag earns you wallet points — 1 point = ₹1, use it on your next order or top-up.</p>
              <div className="flex gap-3 mt-6 flex-wrap">
                <Link to="/partner/signup" className="inline-flex items-center gap-2 rounded-full bg-white text-navy px-5 py-2.5 font-semibold text-sm hover:bg-blue-50">Get your link <ArrowRight size={14} /></Link>
                <Link to="/dashboard" className="inline-flex items-center gap-2 rounded-full border border-white/30 px-5 py-2.5 font-semibold text-sm hover:bg-white/10">Open wallet</Link>
              </div>
            </div>
            <div className="relative grid grid-cols-2 gap-3">
              <div className="card-soft p-4 !bg-white/10 !border-white/15 backdrop-blur-sm">
                <p className="text-xs text-blue-200 uppercase tracking-widest">You share</p>
                <p className="h-display text-2xl mt-1">Referral link</p>
              </div>
              <div className="card-soft p-4 !bg-white/10 !border-white/15 backdrop-blur-sm">
                <p className="text-xs text-blue-200 uppercase tracking-widest">Friend saves</p>
                <p className="h-display text-2xl mt-1">Peace of mind</p>
              </div>
              <div className="card-soft p-4 !bg-white/10 !border-white/15 backdrop-blur-sm">
                <p className="text-xs text-blue-200 uppercase tracking-widest">You earn</p>
                <p className="h-display text-2xl mt-1">₹ Cashback</p>
              </div>
              <div className="card-soft p-4 !bg-white/10 !border-white/15 backdrop-blur-sm">
                <p className="text-xs text-blue-200 uppercase tracking-widest">Everyone</p>
                <p className="h-display text-2xl mt-1">Wins 🎉</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center">
            <p className="eyebrow mb-4">Loved by owners</p>
            <h2 className="h-display text-4xl md:text-5xl">Real stories from <em className="font-serif-display italic font-normal text-navy">Connect Near Me</em> users</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="card-soft p-6">
                <div className="flex gap-0.5 mb-3">{[...Array(5)].map((_, i) => <Star key={i} size={13} className="fill-yellow-400 text-yellow-400" />)}</div>
                <p className="text-neutral-700">&ldquo;{t.quote}&rdquo;</p>
                <div className="mt-4 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-navy/10 text-navy flex items-center justify-center font-semibold">{t.name[0]}</div>
                  <div><p className="text-sm font-semibold">{t.name}</p><p className="text-xs text-neutral-500">{t.city}</p></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center text-white">
          <h2 className="h-display text-4xl md:text-5xl">Stick a Connect tag. <em className="font-serif-display italic font-normal text-blue-200">Drive worry-free.</em></h2>
          <p className="mt-4 text-blue-100">Bike &#8377;299 &middot; Car &#8377;499 &middot; Combo &#8377;699 &middot; inclusive of all features.</p>
          <Link to="/shop" className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-navy py-3 px-6 font-semibold hover:bg-blue-50 transition-colors">Shop now <ArrowRight size={16} /></Link>
        </div>
      </section>

      <PartnerContactModal open={partnerModalOpen} onClose={() => setPartnerModalOpen(false)} />
    </Layout>
  );
}
